import { ReactNode, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Refresh-safe gate.
 *
 * On a hard refresh of any deep route (e.g. /admin/replies), Supabase needs a
 * tick to rehydrate the persisted session from localStorage. AuthContext sets
 * `loading=false` once the first auth event fires, but during that brief
 * window `user` may be null even though the user is in fact still signed in.
 *
 * Previously this component navigated to /auth as soon as
 * `loading === false && !user`, which sent valid Super Admin sessions to the
 * login page. We now perform a defensive recheck of `supabase.auth.getSession()`
 * before redirecting, and only navigate away if there truly is no session.
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [recheckDone, setRecheckDone] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (user) {
      setRecheckDone(true);
      return;
    }

    // user is null after auth resolved — verify with a direct session check
    // before redirecting. Supabase persists the session in localStorage; this
    // call returns synchronously fast in practice.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        if (!data.session) {
          // Preserve the originally requested URL so login can return here.
          const from = `${location.pathname}${location.search}${location.hash}`;
          navigate("/auth", { replace: true, state: { from } });
        }
        // If session exists, AuthContext will fire onAuthStateChange shortly
        // and `user` will become non-null on next render — just stop showing
        // a redirect-pending state.
        setRecheckDone(true);
      } catch {
        if (!cancelled) setRecheckDone(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, loading, navigate, location.pathname, location.search, location.hash]);

  // Keep showing the spinner while:
  // - auth itself is still loading, OR
  // - auth finished with no user but we haven't run the defensive recheck, OR
  // - the defensive recheck found a session that AuthContext hasn't reflected yet.
  //   (rendering children here would let AdminRoute mount with empty role state
  //    and bounce to "/")
  if (loading || (!user && !recheckDone)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return <>{children}</>;
};
